/**
 * One palette for every edge on the flow canvas.
 *
 * Edges used to be styled at each of the ~35 places one gets pushed, and the values
 * drifted: the "yes" path was painted `'green'` (#008000) in three spots, `'#22c55e'`
 * in three more and `'#38A169'` in another, with "no" split across `'red'` (#ff0000),
 * `'#ef4444'` and `'#E53E3E'`. Widths drifted too — 3 nearly everywhere, 2 in the
 * templates. Same branch, different colour depending on which code path built it.
 *
 * So colour is derived here instead, from the two things an edge already knows: which
 * handle it leaves (`sourceHandle`) and what kind of node that is. Call sites no longer
 * set `style` at all — `withEdgeStyles` stamps it on at the end.
 *
 * Note that the CONNECTOR DOTS are not coloured from here. ../flowHandles.css paints
 * every handle one flat black, deliberately, so a handle reads as a handle wherever it
 * is. Edges are the surface that carries colour: NODE_ACCENTS below is each node type's
 * own header/border colour, read off the node components in ../nodes, so an edge leaving
 * a node is the colour of the node it leaves.
 */

import { PRIMARY_BG } from '../../../theme/brand';

/** Matches the portal canvas, and what most of these call sites already used. */
export const EDGE_STROKE_WIDTH = 2;

/** Truthy branch: condition yes, API success, consent enabled, flow completed. */
export const BRANCH_YES = '#22c55e';

/** Falsy branch: condition no, API error, consent disabled. */
export const BRANCH_NO = '#ef4444';

/**
 * Waited and nothing came back. Deliberately NOT red — no reply to an SMS or an
 * unclicked button is an ordinary outcome, not a failure, and painting it red made
 * every canvas with an inbound node look broken.
 */
export const BRANCH_TIMEOUT = '#f6ad55';

/** Catch-all/else paths, which carry no verdict of their own. */
export const BRANCH_NEUTRAL = '#9ca3af';

/**
 * Per-node-type accent: each node's own header/border colour, as its component in
 * ../nodes actually renders it.
 *
 * The three brand-primary types read PRIMARY_BG rather than a literal, because the
 * primary colour comes from REACT_APP_PRIMARY_COLOR at build time; a hardcoded #2563eb
 * would leave blue edges on the STC purple build.
 */
export const NODE_ACCENTS = {
  startNode: '#00c951',
  // End is terminal — nothing leaves it — so this never actually paints an edge. It is
  // here so the map stays a complete list of node types. Chakra gray.400 (#CBD5E0) is
  // the node's real colour; kept darker so it is visible if End ever gains an output.
  endNode: '#9ca3af',
  actionNode: PRIMARY_BG,
  conditionNode: '#fbaf3f',
  enhancedConditionNode: '#fbaf3f',
  smartConditionNode: '#8b5cf6',
  delayNode: '#a475f9',
  dynamicDelayNode: '#8b5cf6',
  apiNode: '#9810fa',
  abTestNode: PRIMARY_BG,
  // Chakra green.500, which is what the node's header and border actually render.
  // Was #22c55e here — identical to BRANCH_YES, so an Update Profile node's edge was
  // indistinguishable from a condition's "yes" branch.
  updateProfileNode: '#38A169',
  customerProfileNode: '#fbaf3f',
  messageReadNode: '#009966',
  buttonClickedNode: '#7c86ff',
  dtmfNode: '#fbaf3f',
  ivrNode: PRIMARY_BG,
  smsInboundNode: '#10b981',
  callFlowNode: '#0d9488',
  callWhatsappFlowNode: '#25d366',
  multiStepNode: '#9333ea',
  mergeNode: '#8b5cf6',
  checkChannelConsentNode: '#7c3aed',
  updateChannelConsentNode: '#c026d3',
};

/** Used when a node type is missing from the map above, or unknown. */
const FALLBACK_ACCENT = '#b1b1b7';

const YES_HANDLES = new Set(['yes', 'true', 'success', 'enabled', 'completed']);
const NO_HANDLES = new Set(['no', 'false', 'error', 'disabled']);
const TIMEOUT_HANDLES = new Set(['timeout', 'no_click', 'no_response']);
const NEUTRAL_HANDLES = new Set(['default', 'fallback', 'else']);

/**
 * The colour an edge should be, given the handle it leaves and the node type it
 * leaves from.
 *
 * Order matters. The exact-id sets are checked BEFORE the `_true`/`_false` suffixes so
 * that `no_click` lands on timeout rather than being read as a negative branch.
 *
 * Anything not a verdict — a Multi Step `step-2`, a Smart Condition `group-*`, an A/B
 * variant, a Button Clicked `btn1_clicked`, a plain edge with no handle at all — takes
 * the source node's own accent. Those handles pick *which* path, not whether one
 * succeeded, so a green/red reading would be inventing a verdict that isn't there.
 */
export function edgeAccent(sourceHandle, sourceNodeType) {
  const handle = sourceHandle || '';

  if (YES_HANDLES.has(handle)) return BRANCH_YES;
  if (NO_HANDLES.has(handle)) return BRANCH_NO;
  if (TIMEOUT_HANDLES.has(handle)) return BRANCH_TIMEOUT;
  if (NEUTRAL_HANDLES.has(handle)) return BRANCH_NEUTRAL;

  // Button Clicked emits `<buttonId>_true` / `<buttonId>_false` per button.
  if (handle.endsWith('_true')) return BRANCH_YES;
  if (handle.endsWith('_false')) return BRANCH_NO;

  return NODE_ACCENTS[sourceNodeType] || FALLBACK_ACCENT;
}

/** The full `style` object for an edge. */
export function edgeStyle(sourceHandle, sourceNodeType) {
  return { strokeWidth: EDGE_STROKE_WIDTH, stroke: edgeAccent(sourceHandle, sourceNodeType) };
}

/** Geometry shared by every edge the builder creates. */
export const EDGE_DEFAULTS = {
  type: 'smoothstep',
  animated: true,
};

/**
 * Restyles a list of edges in place of whatever `style` they were built with.
 *
 * Runs as a last pass over a finished edge list so the ~35 call sites that push edges
 * do not each have to get this right. `nodes` supplies the source node's type; pass the
 * same array the edges belong to.
 *
 * An existing `labelStyle` keeps its other properties and has only its `fill` pulled
 * onto the stroke colour — a label is not added where there wasn't one, so edges that
 * relied on React Flow's default label styling still look the way they did.
 */
export function withEdgeStyles(edges, nodes) {
  const typeById = new Map((nodes || []).map((node) => [node.id, node.type]));

  return (edges || []).map((edge) => {
    const stroke = edgeAccent(edge.sourceHandle, typeById.get(edge.source));
    return {
      ...edge,
      ...EDGE_DEFAULTS,
      style: { ...edge.style, strokeWidth: EDGE_STROKE_WIDTH, stroke },
      ...(edge.labelStyle ? { labelStyle: { ...edge.labelStyle, fill: stroke } } : null),
    };
  });
}
